import { inject, Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { catchError, from, interval, map, Observable, of, switchMap, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthCredentials, AuthSession, AuthUser } from '../../model/app.model';
import { AuthStateService } from './auth-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authState = inject(AuthStateService);

  private _refreshTokenInProgress = false;

  constructor() {
    this.configure();

    interval(environment.awsCognitoConfig.tokenRefreshCheckPeriod).subscribe(() => {
      this.checkAndTryRefreshToken();
    });
  }

  private configure(): void {
    const cfg = environment.awsCognitoConfig;
    Amplify.configure({
      Auth: {
        Cognito: {
          identityPoolId: cfg.identityPoolId,
          userPoolId: cfg.userPoolId,
          userPoolClientId: cfg.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: cfg.oauth.domain,
              scopes: cfg.oauth.scope,
              redirectSignIn: [cfg.oauth.redirectSignIn],
              redirectSignOut: [cfg.oauth.redirectSignOut],
              responseType: cfg.oauth.responseType as 'code' | 'token',
            },
          },
        },
      },
    });
  }

  isAuthenticatedGetAuthSession(): Observable<AuthSession | undefined> {
    return from(fetchAuthSession()).pipe(
      map((session) => {
        if (!session.tokens || !session.credentials) {
          return undefined;
        }

        const payload = session.tokens.idToken?.payload ?? {};

        const user: AuthUser = {
          idToken: {
            jwtToken: session.tokens.idToken?.toString() ?? '',
            expiration: (payload['exp'] as number) ?? 0,
            issuedAt: (payload['iat'] as number) ?? 0,
          },
          groups: (payload['cognito:groups'] as string[]) ?? [],
          userType: (payload['custom:user_type'] as string) ?? '',
          vendor: (payload['custom:vendor'] as string) ?? '',
        };

        const credentials: AuthCredentials = {
          accessKeyId: session.credentials.accessKeyId,
          secretAccessKey: session.credentials.secretAccessKey,
          sessionToken: session.credentials.sessionToken ?? '',
          identityId: session.identityId ?? '',
          authenticated: true,
          expiration: session.credentials.expiration,
        };

        return { credentials, user };
      }),
      catchError((err) => {
        console.error('Auth session error', err);
        return of(undefined);
      }),
    );
  }

  federatedSignIn(): void {
    signInWithRedirect().catch((err) => {
      console.error('[Auth] OAuth sign-in error', err);
    });
  }

  signOut(): Observable<void> {
    return from(signOut()).pipe(take(1));
  }

  private checkAndTryRefreshToken(): void {
    const session = this.authState.session();
    if (this._refreshTokenInProgress || !this.authState.isAuthenticated() || !session) {
      return;
    }

    const now = Date.now();
    const expiration = session.user.idToken.expiration * 1000;
    const issuedAt = session.user.idToken.issuedAt * 1000;
    const duration = expiration - issuedAt;
    const expiresIn = expiration - now;
    const expiresForRefreshIn =
      issuedAt + duration * environment.awsCognitoConfig.tokenDurationSafeBufferFactor - now;

    if (expiresIn <= 0) {
      this.federatedSignIn();
    } else if (expiresForRefreshIn < 0) {
      this.refreshToken();
    }
  }

  private refreshToken(): void {
    this._refreshTokenInProgress = true;
    from(fetchAuthSession({ forceRefresh: true }))
      .pipe(
        take(1),
        switchMap(() => this.isAuthenticatedGetAuthSession()),
        take(1),
      )
      .subscribe({
        next: (session) => {
          if (session) {
            this.authState.setSession(session);
          }
          this._refreshTokenInProgress = false;
        },
        error: () => {
          this._refreshTokenInProgress = false;
        },
      });
  }
}
